import { Recommendations } from '@sparknz/journeys-common/dist/components/SubscriptionComparison/typings';

import { IfpAccountSelectorQuery$data } from '__generated__/IfpAccountSelectorQuery.graphql';
import { PaymentOptionsAssociatedPriceRulesQuery$data } from '__generated__/PaymentOptionsAssociatedPriceRulesQuery.graphql';
import { PaymentOptionsQuery } from '__generated__/PaymentOptionsQuery.graphql';
import { PlanOptionsQuery } from '__generated__/PlanOptionsQuery.graphql';
import { ProductDetailsQuery } from '__generated__/ProductDetailsQuery.graphql';
import { WearableEligibilityAccountSelectorQuery$data } from '__generated__/WearableEligibilityAccountSelectorQuery.graphql';
import { TrackingCart } from 'shared/types/cart';

export enum InfoLayerStatus {
  ContentFragment = 'CONTENT_FRAGMENT',
  RawHtml = 'RAW_HTML',
  Close = 'CLOSE',
}

export type InfoLayerType = {
  contentKey?: string;
  isNested?: boolean;
  status: InfoLayerStatus;
};

export enum ActionLayerStatus {
  Close = 'CLOSE',
  ShowNewPlan = 'SHOW_NEW_PLAN',
  ShowExistingPlan = 'SHOW_EXISTING_PLAN',
}

export enum IfpEligibilityCheckSteps {
  CheckEligibility = 'ELIGIBILITY_CHECK',
  Login = 'LOGIN',
  AccountSelection = 'ACCOUNT_SELECTION',
  LineSelection = 'LINE_SELECTION',
  PlanSelection = 'PLAN_SELECTION',
  ReviewPlan = 'REVIEW_PLAN',
  DisableIfp = 'DISABLE_IFP',
  AddToCart = 'ADD_TO_CART',
  IneligiblePlan = 'INELIGIBLE_PLAN',
}

export type IfpEligibilityType = {
  activeStep: IfpEligibilityCheckSteps;
  isCheckRequired?: boolean;
  eligibility?: IfpAccountSelectorQuery$data['me']['ifpEligibility'];
  selectedAccountNumber?: string;
  selectedLineNumber?: string;
  existingOfferId?: string;
  sections?: Section[];
  selectedLinePlanId?: string;
  selectedPlanTitle?: string;
  isChangePlan?: boolean;
  isChangePlanOfferDetailsLoading?: boolean;
  recommendations?: Recommendations;
  ineligibleOfferIds?: string[];
};

export enum WearableEligibilityCheckSteps {
  CheckEligibility = 'ELIGIBILITY_CHECK',
  Login = 'LOGIN',
  AccountSelection = 'ACCOUNT_SELECTION',
  LineSelection = 'LINE_SELECTION',
  AddToCart = 'ADD_TO_CART',
}

export type WearableEligibilityType = {
  activeStep: WearableEligibilityCheckSteps;
  isCheckRequired?: boolean;
  eligibility?: WearableEligibilityAccountSelectorQuery$data['me']['wearableEligibility'];
  selectedAccountNumber?: string;
  selectedLineNumber?: string;
  sections?: WearableEligibilitySection[];
};

export type WearableIneligibleLinesReason =
  WearableEligibilityType['eligibility']['ineligibleLines'][0]['reason'];

export type EligibilityAccounts =
  WearableEligibilityType['eligibility']['accounts'];

export type EligibleLines = IfpEligibilityType['eligibility']['eligibleLines'];

export type IneligibleLines =
  IfpEligibilityType['eligibility']['ineligibleLines'];

export type IneligibleLinesReason =
  IfpEligibilityType['eligibility']['ineligibleLines'][0]['reason'];

export interface InputOption {
  label: string;
  value: string;
}

export enum PlanBundleOptions {
  ChooseNewPlan = 'NEW_PLAN',
  UseExistingPlan = 'EXISTING_PLAN',
  DeviceOnly = 'DEVICE_ONLY',
}

export enum PrimaryCategory {
  HANDSET = 'handsets',
  WEARABLES = 'wearables',
  TABLET = 'tablet',
  ACCESSORIES = 'accessories',
  DATA_DEVICES = 'data_devices',
}

/**
 Device Variants(typing for product variants which product offers are group by groupIds, e.g device variants are grouped by model)
 */

export type FeaturesType = 'color' | 'colorHex' | 'storage';

export type DeviceOfferDetails =
  ProductDetailsQuery['response']['productData']['offerDetails'];

export type DeviceOfferDetail = DeviceOfferDetails[0];

export type DeviceAssociatedPrices = DeviceOfferDetail['associatedPrices'];

export type DeviceAssociatedPrice = DeviceAssociatedPrices[0];

export type DeviceAssociatedPriceRules =
  PaymentOptionsAssociatedPriceRulesQuery$data['priceRulesData']['priceRules'];

export type DeviceAssociatedPriceRule = DeviceAssociatedPriceRules[0];

/**
 Payment options, IFP typings
 */

export type IfpOfferDetails = PaymentOptionsQuery['response']['relatedIfp'];

export type IfpOfferDetail = IfpOfferDetails[0];

export type IfpPromotions = IfpOfferDetail['promotions'];

export type IfpPromotion = IfpPromotions[0];

export interface PaymentOption {
  id: string;
  name: string;
  description: string;
  associatedPriceRuleDescription?: string;
  basePrice: number;
  basePriceExcludingTax: number;
  effectivePrice: number;
  effectivePriceExcludingTax: number;
  priceFrequencyPeriod?: string;
  priceFrequencyValue?: number;
  priceLength?: number;
  priceType?: string;
  promotions?: IfpPromotions;
  downPaymentRequired?: boolean;
  minimumDownPaymentAmount?: number;
}

/**
 Plan options, plan offer typings
 */

export type PlanOfferDetails =
  PlanOptionsQuery['response']['relatedPlans']['bundles'][0]['offerDetails'];

export type PlanOfferDetail = PlanOfferDetails[0];

export type PlanEntitlement = PlanOfferDetails[0]['entitlements'][0];

export type ProductDetailPromotions = PlanOfferDetail['promotions'];

export type Price = PlanOfferDetail['price'];

export type EventInfo = {
  eventName: string;
  eventAction: string;
};

export interface LinkedProductsTrackingArgs {
  device: DeviceOfferDetail;
  eventInfo?: EventInfo;
  cart?: TrackingCart;
  plan?: PlanOfferDetail;
  paymentOption?: PaymentOption;
  isBusiness?: boolean;
  isChangePlan?: boolean;
  isSpotifyOptIn?: boolean;
}

export interface Section {
  id: number;
  name: string;
  value: Partial<IfpEligibilityCheckSteps>;
}

interface QuicklinksCardItem {
  _path: string;
  title: string;
  imagePath: {
    _path: string;
  };
  description: string;
  linkLabel: string;
  linkUrl: string;
  target: string;
}

interface QuickLinksCardModelList {
  items: QuicklinksCardItem[];
}
export interface QuickLinksCardListData {
  data: {
    quicklinksCardModelList: QuickLinksCardModelList;
  };
}

export interface WearableEligibilitySection {
  id: number;
  name: string;
  value: WearableEligibilityCheckSteps;
}

export enum WearablePlans {
  Apple = 'one_number_endless_wearable_plan',
  Samsung = 'endless_wearable_plan',
  Spacetalk = 'smart_plan',
}
